// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import UtilisateurService from '../../services/UtilisateurService';
import RoleService from '../../services/RoleService';

const UtilisateursListItems = () => {
	const [originalUtilisateurs, setOriginalUtilisateurs] = useState([]);
	const [utilisateurs, setUtilisateurs] = useState([]);
	const [role, setRole] = useState([]);

	useEffect(() => {
		Promise.all([
			UtilisateurService.getAllUtilisateurs(),
			RoleService.getAllRoles()
		]).then(([utilisateursData, roleData]) => {
			setOriginalUtilisateurs(utilisateursData);
			setUtilisateurs(utilisateursData);
			setRole(roleData);
		});
	}, []);

	const formatRole = (value) => {
		return (
			value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
		);
	};

	const formatValue = (value, columnId) => {
		if (columnId === 'role' && value && typeof value === 'object') {
			return value.nom ? formatRole(value.nom) : '';
		}

		if (typeof value === 'string') {
			if (columnId === 'last_name') {
				return value.toUpperCase();
			}
			if (columnId === 'username') {
				return value.toLowerCase();
			}
			return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
		}
		return value;
	};

	const getRoleById = (roleId) => {
		return role.find((item) => item.id === roleId) ?? {};
	};

	const getInitials = (utilisateur) => {
		const firstName = utilisateur.last_name
			? utilisateur.last_name.charAt(0).toUpperCase()
			: '';
		const lastName = utilisateur.first_name
			? utilisateur.first_name.charAt(0).toUpperCase()
			: '';
		return `${lastName} ${firstName}`;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'last_name',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					return (
						<div className="d-flex align-items-center">
							<Avatar
								type="initial"
								size="md"
								name={initials}
								className="rounded-circle"
								alt={initials}
							/>
							{value ? (
								<Link to={`/administration/utilisateurs/${row.original.id}`}>
									<h5 className="mb-0 ms-2">
										{formatValue(value, 'last_name')}
									</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'first_name',
				Header: 'Prénom',
				Cell: ({ value }) => <div>{formatValue(value, 'first_name')}</div>
			},
			{
				accessor: 'username',
				Header: "Nom d'utilisateur",
				Cell: ({ value }) => <div>{formatValue(value, 'username')}</div>
			},
			{
				accessor: 'role',
				Header: 'Rôle',
				Cell: ({ value }) => (
					<div>
						{value && typeof value === 'object'
							? formatValue(value.nom, 'role')
							: ''}
					</div>
				)
			},
			{
				accessor: 'email',
				Header: 'Email',
				Cell: ({ value }) => <div>{value}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return utilisateurs.map((utilisateur) => ({
			...utilisateur,
			role: getRoleById(utilisateur.role)
		}));
	}, [utilisateurs]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xl={2} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un utilisateur"
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default UtilisateursListItems;
